import React, { useState, useEffect } from "react";
import { Select, Button, Box, Input, Modal } from "@sainsburys-tech/fable";
import Headerfile from "../../common/Headerfile";
import Footerfile from "../../common/Footerfile";
import Styles from "./AddPrinter.module.css";
import { useNavigate } from "react-router-dom";
import { useGetPostsQuery, useNewPostMutation } from "../../../api/apiConfig";
import { getLogisticsRole } from "../../../utils/authToken";
import { USER_ROLE } from "../../../constants/roles";
import Unauthorized from "../../unauthorized/Unauthorized";

const AddPrinter = () => {
  interface IState {
    IsRetired: boolean;
    Status: string;
    Configuration: {
      IP: string;
      IsPublished: boolean;
      IsShared: boolean;
      IsQueuingEnabled: boolean;
      Port: number;
      Type: string;
      LabelMediaType: string;
    };
    Identity: {
      Name: string;
      SBPrinterId: string;
      SerialNumber: string;
      Location: {
        Id: string;
        Name: string;
        WarehouseFloor: string;
        WarehouseLocality: string;
      };
      PrinterModel: {
        Id: string;
        Name: string;
      };
    };
    StatusOverride: {
      IsAllowed: boolean;
      PreventTill: null;
      StatusCode: string;
      StatusDescription: string;
      LastUpdate: Date;
    };
  }

  const navigate = useNavigate();

  const [newPost] = useNewPostMutation();
  const [apiHeading, setApiHeading] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

  const loggedUserRole = getLogisticsRole();

  const isDisabled =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
      loggedUserRole?.includes(USER_ROLE.READ_WRITE)
      ? false
      : true;

  useEffect(() => {
    setTimeout(() => {
      fetchAllPrinterModels();
    }, 2000);
  }, []);

  const [values, setValues] = useState<IState>({
    IsRetired: false,
    Status: "A",
    Configuration: {
      IP: "",
      IsPublished: true,
      IsShared: false,
      IsQueuingEnabled: false,
      Port: 9100,
      Type: "",
      LabelMediaType: ""
    },
    Identity: {
      Name: "",
      SBPrinterId: "",
      SerialNumber: "",
      Location: {
        Id: "",
        Name: "default",
        WarehouseFloor: "",
        WarehouseLocality: ""
      },
      PrinterModel: {
        Id: "",
        Name: ""
      }
    },
    StatusOverride: {
      IsAllowed: true,
      PreventTill: null,
      StatusCode: "",
      StatusDescription: "",
      LastUpdate: new Date()
    }
  });

  const initialState: IState = {
    IsRetired: false,
    Status: "I",
    Configuration: {
      IP: "",
      IsPublished: false,
      IsShared: false,
      IsQueuingEnabled: false,
      Port: 9100,
      Type: "",
      LabelMediaType: ""
    },
    Identity: {
      Name: "",
      SBPrinterId: "",
      SerialNumber: "",
      Location: {
        Id: "",
        Name: "default",
        WarehouseFloor: "",
        WarehouseLocality: ""
      },
      PrinterModel: {
        Id: "",
        Name: ""
      }
    },
    StatusOverride: {
      IsAllowed: true,
      PreventTill: null,
      StatusCode: "",
      StatusDescription: "",
      LastUpdate: new Date()
    }
  };
  interface IErrors {
    IsRetired?: string;
    Status?: string;
    Configuration?: {
      IP?: string;
      IsPublished?: string;
      IsShared?: string;
      Port?: number;
      Type?: string;
      LabelMediaType?: string;
    };
    Identity?: {
      Name?: string;
      SBPrinterId?: string;
      SerialNumber?: string;
      Location?: {
        Name?: string;
        WarehouseFloor?: string;
        WarehouseLocality?: string;
      };
      PrinterModel?: {
        Name?: string;
      };
    };
  }

  const [errors, setErrors] = useState<IErrors>({});
  let [count] = useState<number>(1);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const { isLoading, data, refetch } = useGetPostsQuery(
    `printers/${page}/${rowsPerPage}`
  );

  const handleNextPage = () => {
    if (data && page < Math.ceil(data.TotalRecords / rowsPerPage)) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (data && page > 1) {
      setPage(page - 1);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const [location, setLocation] = useState<Location[]>([]);
  const [printerModel, setPrinterModel] = useState<PrinterModel[]>([]);

  const {
    isLoading: isLocationLoading,
    isSuccess: isLocationSuccess,
    data: getAllLocations,
    refetch: fetchAllLocations
  } = useGetPostsQuery("locations/GetLocationFromIdentityToken");

  const {
    isLoading: isPrinterModelLoading,
    isSuccess: isPrinterModelSuccess,
    data: printerModelList,
    refetch: fetchAllPrinterModels
  } = useGetPostsQuery("printer-models");

  useEffect(() => {
    fetchAllLocations();
  }, []);

  interface Location {
    LocationId: string;
    Name: string;
    Status: string;
  }

  interface PrinterModel {
    PrinterModelId?: string;
    Name: string;
  }

  useEffect(() => {
    if (isLocationSuccess && getAllLocations?.Model) {
      const validLocations = getAllLocations.Model.filter(
        (location: any): location is Location =>
          location.LocationId &&
          location.Name &&
          location.Status
      );

      const sortedLocations: Location[] = validLocations.sort(
        (a: any, b: any) => a.Name.localeCompare(b.Name)
      );
      setLocation(sortedLocations);
    }
    if (isPrinterModelSuccess && printerModelList?.Model) {
      const sortedPrinterModels = [
        ...(printerModelList.Model as PrinterModel[])
      ].sort((a, b) => a?.Name.localeCompare(b?.Name));
      setPrinterModel(sortedPrinterModels);
    }
  }, [isLocationSuccess, isPrinterModelSuccess, count]);

  useEffect(() => {
    refetch();
  }, []);

  const handleChange = (name: string, value: string | boolean | File) => {
    const keys = name.split(".");
    if (keys.length === 1) {
      setValues({ ...values, [name]: value });
    } else {
      setValues((prevValues) => {
        let updatedValues: any = { ...prevValues };
        let current: any = updatedValues;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return updatedValues;
      });
    }
  };

  interface IData {
    StatusCode: number;
    Status: string;
    TotalRecords?: number;
    Message?: string;
    Model?: any;
    Value?: string;
  }

  interface IResponse {
    data: IData;
    TotalRecords: number;
  }

  function isIResponse(response: any): response is IResponse {
    return response.data !== undefined && typeof response.data === "object";
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitted(true);
    const validationErrors = validate(values);
    values.Identity.SBPrinterId = values.Identity.Name;

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSaveButtonDisabled(true);
      try {
        const response = await newPost({
          Method: "printers",
          ...values
        });
        if (
          isIResponse(response) &&
          response.data.StatusCode === 0 &&
          response.data.Status === "Ok"
        ) {
          setIsSaveButtonDisabled(false);
          setApiMessage("Printer created successfully" || null);
          setApiHeading("Success!!!");
          setIsOpen(true);
          setValues(initialState);
          refetch();
        } else if (isIResponse(response) && response.data) {
          setIsSaveButtonDisabled(false);
          setApiMessage(response.data.Message || null);
          setApiHeading("Error!!!");
          setIsOpen(true);
          console.error("Error response:", response);
        } else {
          setIsSaveButtonDisabled(false);
          console.error("Something Went Wrong..", response);
        }
      } catch (error) {
        setIsSaveButtonDisabled(false);
        console.error(error);
      }
    }
  };

  const validate = (values: IState) => {
    let errors: any = {};
    const ipRegex =
      /^((25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)$/;
    if (!values.Identity.Name) {
      errors.Identity = {
        ...errors.Identity,
        Name: "Printer Name is required"
      };
    }

    if (
      !values.Identity.Location.Name ||
      values.Identity.Location.Name === "default"
    ) {
      errors.Identity = {
        ...errors.Identity,
        Location: { ...errors.Identity.Location, Name: "Location is required" }
      };
    }

    if (
      !values.Identity.PrinterModel.Name ||
      values.Identity.PrinterModel.Name === "default"
    ) {
      errors.Identity = {
        ...errors.Identity,
        PrinterModel: {
          ...errors.Identity.PrinterModel,
          Name: "Printer Model is required"
        }
      };
    }

    if (
      !values.Configuration?.Type ||
      values.Configuration?.Type === "default"
    ) {
      errors.Configuration = {
        ...errors.Configuration,
        Type: "Printer Type is required"
      };
    }

    if (values.Configuration?.Type === "Z") {
      if (!values.Configuration?.Port) {
        errors.Configuration = {
          ...errors.Configuration,
          Port: "Port is required"
        };
      } else if (!/^\d+$/.test(values.Configuration.Port.toString())) {
        errors.Configuration = {
          ...errors.Configuration,
          Port: "Port number must only contain digits"
        };
      } else if (
        values.Configuration.Port.toString().length < 1 ||
        values.Configuration.Port.toString().length > 5
      ) {
        errors.Configuration = {
          ...errors.Configuration,
          Port: "Port number must be between 1 to 5 digits"
        };
      }

      if (!values.Configuration.IP) {
        errors.Configuration = {
          ...errors.Configuration,
          IP: "IP Address is required"
        };
      } else if (!ipRegex.test(values.Configuration.IP)) {
        errors.Configuration = {
          ...errors.Configuration,
          IP: "IP Address is invalid"
        };
      }
    } else {
      if (!values.Configuration?.Port) {
        errors.Configuration = {
          ...errors.Configuration,
          Port: "Port is required"
        };
      }

      if (!values.Configuration.IP) {
        errors.Configuration = {
          ...errors.Configuration,
          IP: "IP Address is required"
        };
      }
    }

    return errors;
  };

  function toSentenceCase(str: string) {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
  }

  const handleLocationChange = (value: string) => {
    const selectedOption = location.find((loc) => loc.Name === value);
    handleChange("Identity.Location.Name", value);
    handleChange("Identity.Location.Id", selectedOption?.LocationId || "");
  };

  const handlePrinterModelChange = (value: string) => {
    const selectedOption = printerModel.find((model) => model.Name === value);
    handleChange("Identity.PrinterModel.Name", value);
    handleChange("Identity.PrinterModel.Id", selectedOption?.PrinterModelId || "");
  };

  return (
    <div>
      <div className={Styles.container}>
        <Headerfile />
        {isDisabled ? (
          <Unauthorized />
        ) : (
          <Box as="div" className={Styles.box}>
            <form onSubmit={handleSubmit} className={Styles.formContainer}>
              <h5 className={Styles.heading}>Add Printer</h5>
              <p className={Styles.rightAlign}>
                <span className={Styles.redAsterisk}>*</span>Indicates required
                field
              </p>{" "}
              <Select
                label="Location*"
                options={[
                  { label: "Select Location", value: "default" },
                  ...(isLocationLoading
                    ? [
                      {
                        label: "Loading...",
                        value: "loading",
                        isDisabled: isLocationLoading
                      }
                    ]
                    : location?.map((loc) => ({
                      value: loc?.Name ?? "",
                      label: loc?.Name ?? "",
                      id: loc?.LocationId ?? ""
                    })) ?? [])
                ]}
                style={{ textAlign: "left" }}
                name="Identity.Location.Name"
                onChange={(value) => handleLocationChange(value)}
                value={values.Identity.Location.Name}
              />
              {isSubmitted && errors.Identity?.Location?.Name && (
                <div
                  data-testid="error-Identity.Location.Name"
                  className={Styles.error}
                >
                  {errors.Identity.Location.Name}
                </div>
              )}
              <br />
              <Select
                label="Printer Model*"
                options={[
                  { label: "Select Printer Model", value: "default" },
                  ...(isPrinterModelLoading
                    ? [
                      {
                        label: "Loading...",
                        value: "loading",
                        isDisabled: isPrinterModelLoading
                      }
                    ]
                    : printerModel?.map((model) => ({
                      label: model?.Name ?? "",
                      value: model?.Name ?? "",
                      id: model?.PrinterModelId ?? ""
                    })) ?? [])
                ]}
                style={{ textAlign: "left" }}
                name="Identity.PrinterModel.Name"
                onChange={(value) => handlePrinterModelChange(value)}
                value={values.Identity.PrinterModel.Name}
              />
              {isSubmitted && errors.Identity?.PrinterModel?.Name && (
                <div
                  data-testid="error-PrinterModel.Name"
                  className={Styles.error}
                >
                  {errors.Identity.PrinterModel.Name}
                </div>
              )}
              <br />
              <Select
                label="Printer Type*"
                options={[
                  { label: "Select Printer Type", value: "default" },
                  { label: "ZPL", value: "Z" },
                  { label: "Others", value: "O" }
                ]}
                style={{ textAlign: "left" }}
                name="Configuration.Type"
                onChange={(e) => handleChange("Configuration.Type", e)}
                value={values.Configuration.Type}
              />
              {isSubmitted && errors.Configuration?.Type && (
                <div
                  data-testid={"error_Configuration.Type"}
                  className={Styles.error}
                >
                  {errors.Configuration.Type}
                </div>
              )}
              <br />
              <label className={Styles.label}>
                <Input
                  name="Identity.Name"
                  label="Printer Name*"
                  autocomplete="off"
                  type="text"
                  onChange={(e) => handleChange("Identity.Name", e)}
                  value={values.Identity.Name}
                />
                {isSubmitted && errors.Identity?.Name && (
                  <div
                    data-testid="error-Identity.Name"
                    className={Styles.error}
                  >
                    {errors.Identity.Name}
                  </div>
                )}
              </label>
              <br />
              <label className={Styles.label}>
                <Input
                  name="Configuration.IP"
                  label="IP Address*"
                  autocomplete="off"
                  type="text"
                  onChange={(e) => handleChange("Configuration.IP", e)}
                  value={values.Configuration.IP}
                />
                {isSubmitted && errors.Configuration?.IP && (
                  <div
                    data-testid={"error-Configuration.IP"}
                    className={Styles.error}
                  >
                    {errors.Configuration.IP}
                  </div>
                )}
              </label>
              <br />
              <label className={Styles.label}>
                <Input
                  name="Configuration.Port"
                  label="Port*"
                  autocomplete="off"
                  type="text"
                  onChange={(e) => handleChange("Configuration.Port", e)}
                  value={values.Configuration.Port.toString()}
                />
                {isSubmitted && errors.Configuration?.Port && (
                  <div
                    data-testid={"error-ConfigurationPort"}
                    className={Styles.error}
                  >
                    {errors.Configuration.Port}
                  </div>
                )}
              </label>
              <br />
              <Input
                name="Identity.Location.WarehouseFloor"
                label="Warehouse"
                autocomplete="off"
                type="text"
                onChange={(e) =>
                  handleChange("Identity.Location.WarehouseFloor", e)
                }
                value={values.Identity.Location.WarehouseFloor}
              />
              {isSubmitted && errors.Identity?.Location?.WarehouseFloor && (
                <div className={Styles.error}>
                  {errors.Identity.Location.WarehouseFloor}
                </div>
              )}
              <br />
              <Input
                name="Identity.Location.WarehouseLocality"
                label="Warehouse Locality"
                autocomplete="off"
                type="text"
                onChange={(e) =>
                  handleChange("Identity.Location.WarehouseLocality", e)
                }
                value={values.Identity.Location.WarehouseLocality}
              />
              {isSubmitted && errors.Identity?.Location?.WarehouseLocality && (
                <div className={Styles.error}>
                  {errors.Identity.Location.WarehouseLocality}
                </div>
              )}
              <br />
              <Select
                label="Label Media Type"
                options={[
                  { label: "Select Label Media Type", value: "default" },
                  { label: "26mm x 26mm", value: "26mm x 26mm" },
                  { label: "51mm x 26mm", value: "51mm x 26mm" },
                  { label: "51mm x 51mm", value: "51mm x 51mm" },
                  { label: "76mm x 26mm", value: "76mm x 26mm" },
                  { label: "76mm x 51mm", value: "76mm x 51mm" },
                  { label: "76mm x 76mm", value: "76mm x 76mm" },
                  { label: "100mm x 26mm", value: "100mm x 26mm" },
                  { label: "100mm x 51mm", value: "100mm x 51mm" },
                  { label: "100mm x 76mm", value: "100mm x 76mm" },
                  { label: "100mm x 100mm", value: "100mm x 100mm" },
                  { label: "100mm x 150mm", value: "100mm x 150mm" }
                ]}
                style={{ textAlign: "left" }}
                name="Configuration.LabelMediaType"
                onChange={(e) =>
                  handleChange("Configuration.LabelMediaType", e)
                }
                value={values.Configuration.LabelMediaType}
              />
              {isSubmitted && errors.Configuration?.LabelMediaType && (
                <div className={Styles.error}>
                  {errors.Configuration.LabelMediaType}
                </div>
              )}
              <br />
              <label className={Styles.label}></label>
              <br />
              <Button
                type="submit"
                className={Styles.button}
                disabled={isSaveButtonDisabled}
              >
                Save
              </Button>
            </form>
            <div className="my-5">
              <h5 className={Styles.heading}>View Printer</h5>
              {isLoading ? (
                <div className={Styles.loadingContainer}>
                  <div className={Styles.loadingSpinner}></div>
                  <div className={Styles.loadingText}>Loading...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-borderless">
                    <thead>
                      <tr>
                        <th>Printer Name</th>
                        <th>IP Address</th>
                        <th>Port</th>
                        <th>Location</th>
                        <th>Printer Model</th>
                        <th>Printer Type</th>
                        <th>Warehouse</th>
                        <th>Warehouse Locality</th>
                        <th>Status</th>
                        <th>Is Published</th>
                        <th>Is Retired</th>
                        <th>Is Shared</th>
                        <th>Is Queuing Enabled</th>
                        <th>Label Media Type</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data && data?.Model?.length > 0 ? (
                        data?.Model.map((printer: any) => (
                          <tr key={printer.Id}>
                            <td>{printer?.Identity?.Name}</td>
                            <td>{printer?.Configuration?.IP}</td>
                            <td>{printer?.Configuration?.Port}</td>
                            <td>{printer?.Identity?.Location?.Name}</td>
                            <td>{printer?.Identity?.PrinterModel?.Name}</td>
                            <td>
                              {" "}
                              {printer?.Configuration?.Type === "Z"
                                ? "ZPL"
                                : "Others"}
                            </td>
                            <td>
                              {printer?.Identity?.Location?.WarehouseFloor}
                            </td>
                            <td>
                              {printer?.Identity?.Location?.WarehouseLocality
                                ? printer?.Identity?.Location?.WarehouseLocality
                                : ""}
                            </td>
                            <td>
                              {printer.Status === "A" ? "Active" : "InActive"}
                            </td>

                            <td>
                              {toSentenceCase(
                                printer?.Configuration?.IsPublished.toString()
                              )}
                            </td>
                            <td>
                              {toSentenceCase(printer?.IsRetired?.toString())}
                            </td>
                            <td>
                              {toSentenceCase(
                                printer?.Configuration?.IsShared.toString()
                              )}
                            </td>
                            <td>
                              {toSentenceCase(
                                (printer?.Configuration?.IsQueuingEnabled ?? false).toString()
                              )}
                            </td>
                            <td>
                              {printer?.Configuration?.LabelMediaType ===
                                "default"
                                ? ""
                                : printer?.Configuration?.LabelMediaType}
                            </td>
                            <td>
                              <button
                                style={{ background: "#007bff" }}
                                onClick={() =>
                                  navigate(`/editprinter/${printer.Id}`)
                                }
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className={Styles.printerNotFound}>
                          <td colSpan={14} className="text-center">
                            No Printers Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className={Styles.pagination}>
                    <button onClick={handlePreviousPage} disabled={page === 1}>
                      Previous
                    </button>
                    <span>
                      Page {page} of{" "}
                      {data ? Math.ceil(data.TotalRecords / rowsPerPage) : 0}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={
                        !data ||
                        page === Math.ceil(data.TotalRecords / rowsPerPage)
                      }
                    >
                      Next
                    </button>
                  </div>
                  <div className={Styles.rowsPerPage}>
                    <label htmlFor="rowsPerPage">Rows per page:</label>
                    <select
                      id="rowsPerPage"
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={40}>40</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            <Modal
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              layout="default"
              header={{
                isDismissible: true
              }}
            >
              <div className="text-center fw-400">
                <h3 className="text-center fw-500">{apiHeading}</h3>
                <h5 className="fw-400">{apiMessage}</h5>
              </div>
            </Modal>
          </Box>
        )}
      </div>
      <Footerfile />
    </div>
  );
};

export default AddPrinter;
